<template>
  <div class="relative z-10" aria-labelledby="modal-title" role="dialog" aria-modal="true">
    <div class="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity"></div>
    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
      <div class="flex min-h-full items-center justify-center p-4">
        <div class="relative transform overflow-hidden rounded-lg bg-gray-800 text-white shadow-xl transition-all sm:max-w-3xl w-full">
          <div class="p-6">
            <!-- หัวข้อและปุ่มปิด -->
            <div class="flex justify-between items-center mb-6">
              <h3 class="text-xl font-semibold text-indigo-300">เพิ่มชาวบ้านใหม่</h3>
              <button @click="$emit('cancelModal')" class="text-gray-400 hover:text-white">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <!-- Tab Navigation -->
            <div class="flex border-b border-gray-700 mb-6">
              <button
                v-for="tab in tabs"
                :key="tab.id"
                @click="activeTab = tab.id"
                class="px-4 py-2 text-sm font-medium transition-colors duration-200"
                :class="activeTab === tab.id ? 'text-indigo-200 border-b-2 border-indigo-700' : 'text-gray-400 hover:text-indigo-300'"
              >
                {{ tab.name }}
              </button>
            </div>

            <!-- ฟอร์ม -->
            <form @submit.prevent="handleAddVillager">
              <div class="space-y-4 h-96 overflow-auto mb-6">
                <!-- General Tab -->
                <div v-if="activeTab === 'general'" class="space-y-4">
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label class="block text-sm font-medium mb-1 text-gray-300">ชื่อชาวบ้าน</label>
                      <input 
                        v-model="newVillager.name" 
                        type="text" 
                        class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        required
                      >
                    </div>
                    <div>
                      <label class="block text-sm font-medium mb-1 text-gray-300">เลเวล</label>
                      <select 
                        v-model.number="newVillager.level" 
                        class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        required
                      >
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="4">4</option>
                        <option value="5">5</option>
                      </select>
                    </div>
                    <div>
                      <label class="block text-sm font-medium mb-1 text-gray-300">ประเภทชาวบ้าน</label>
                      <select 
                        v-model="newVillager.type" 
                        class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        required
                      >
                        <option value="นักรบรับจ้าง">นักรบรับจ้าง</option>
                        <option value="อันธพาล">อันธพาล</option>
                      </select>
                    </div>
                    <div>
                      <label class="block text-sm font-medium mb-1 text-gray-300">ลิงค์รูปภาพชาวบ้าน</label>
                      <input 
                        v-model="newVillager.img" 
                        type="url" 
                        class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="https://example.com/image.jpg"
                        required
                      >
                    </div>
                    <div>
                      <label class="block text-sm font-medium mb-1 text-gray-300">ลิงค์รูปหน้าปกติ</label>
                      <input 
                        v-model="newVillager.normalFace" 
                        type="url" 
                        class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder="https://example.com/normal-face.jpg"
                      >
                    </div>
                  </div>
                </div>

                <!-- Phrases & Locations Tab -->
                <div v-if="activeTab === 'phrases'" class="space-y-4">
                  <!-- คำพูดที่ชอบพูด -->
                  <div class="space-y-4 mb-6">
                    <h4 class="text-lg font-medium text-gray-300">คำพูดที่ชอบพูด</h4>
                    <div v-for="(phrase, index) in newVillager.phrases" :key="index" class="grid grid-cols-12 items-center gap-3 animate-fade-in">
                      <div class="col-span-10">
                        <input 
                          v-model="newVillager.phrases[index]" 
                          type="text" 
                          placeholder="คำพูด"
                          class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                      </div>
                      <div class="col-span-2">
                        <button 
                          v-if="index > 0"
                          @click="removePhrase(index)"
                          type="button"
                          class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-200"
                          aria-label="ลบคำพูด"
                        >
                          ลบ
                        </button>
                      </div>
                    </div>
                    <div class="flex gap-3 mt-3">
                      <button 
                        @click="addPhrase"
                        type="button"
                        class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-200"
                      >
                        + เพิ่มคำพูด
                      </button>
                    </div>
                  </div>

                  <!-- สถานที่ตั้งที่สามารถไปได้ -->
                  <div class="space-y-4 mb-6">
                    <h4 class="text-lg font-medium text-gray-300">สถานที่ตั้งที่สามารถไปได้</h4>
                    <div v-for="(location, index) in newVillager.locations" :key="index" class="grid grid-cols-12 items-center gap-3 animate-fade-in">
                      <div class="col-span-10">
                        <input 
                          v-model="newVillager.locations[index]" 
                          type="text" 
                          placeholder="สถานที่ เช่น Forest"
                          class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                      </div>
                      <div class="col-span-2">
                        <button 
                          v-if="index > 0"
                          @click="removeLocation(index)"
                          type="button"
                          class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-200"
                          aria-label="ลบสถานที่"
                        >
                          ลบ
                        </button>
                      </div>
                    </div>
                    <div class="flex gap-3 mt-3">
                      <button 
                        @click="addLocation"
                        type="button"
                        class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-200"
                      >
                        + เพิ่มสถานที่
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Stats & Dice Tab -->
                <div v-if="activeTab === 'stats'" class="space-y-4">
                  <!-- ค่าใช้จ่ายในการจ้าง -->
                  <div class="space-y-4 mb-6">
                    <h4 class="text-lg font-medium text-gray-300">ค่าใช้จ่ายในการจ้าง</h4>
                    <div class="grid grid-cols-3 gap-4">
                      <div>
                        <label class="block text-sm font-medium mb-1 text-gray-300">Silver</label>
                        <input 
                          v-model.number="newVillager.hireCosts.silver" 
                          type="number" 
                          min="0"
                          class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                      </div>
                      <div>
                        <label class="block text-sm font-medium mb-1 text-gray-300">Gold</label>
                        <input 
                          v-model.number="newVillager.hireCosts.gold" 
                          type="number" 
                          min="0"
                          class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                      </div>
                      <div>
                        <label class="block text-sm font-medium mb-1 text-gray-300">Diamond</label>
                        <input 
                          v-model.number="newVillager.hireCosts.diamond" 
                          type="number" 
                          min="0"
                          class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                      </div>
                    </div>
                  </div>

                  <!-- ค่าสถานะ -->
                  <div class="space-y-4 mb-6">
                    <h4 class="text-lg font-medium text-gray-300">ค่าสถานะ ({{ newVillager.stats.stamina + newVillager.stats.str + newVillager.stats.int + newVillager.stats.agi + newVillager.stats.defense + newVillager.stats.luck }})</h4>
                    <div class="grid grid-cols-2 sm:grid-cols-6 gap-4">
                      <div>
                        <label class="block text-sm font-medium mb-1 text-gray-300">Stamina</label>
                        <input
                          v-model.number="newVillager.stats.stamina"
                          type="number"
                          min="0"
                          class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label class="block text-sm font-medium mb-1 text-gray-300">Strength</label>
                        <input
                          v-model.number="newVillager.stats.str"
                          type="number"
                          min="0"
                          class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label class="block text-sm font-medium mb-1 text-gray-300">Agility</label>
                        <input
                          v-model.number="newVillager.stats.agi"
                          type="number"
                          min="0"
                          class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label class="block text-sm font-medium mb-1 text-gray-300">Intelligence</label>
                        <input
                          v-model.number="newVillager.stats.int"
                          type="number"
                          min="0"
                          class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label class="block text-sm font-medium mb-1 text-gray-300">Luck</label>
                        <input
                          v-model.number="newVillager.stats.luck"
                          type="number"
                          min="0"
                          class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label class="block text-sm font-medium mb-1 text-gray-300">Defense</label>
                        <input
                          v-model.number="newVillager.stats.defense"
                          type="number"
                          min="0"
                          class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    </div>
                  </div>

                  <!-- จำนวนลูกเต๋า -->
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                    <div>
                      <label class="block text-sm font-medium mb-1 text-gray-300">จำนวนลูกเต๋าฝ่ายบุก</label>
                      <input 
                        v-model.number="newVillager.attackDice" 
                        type="number" 
                        min="0"
                        class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                    </div>
                    <div>
                      <label class="block text-sm font-medium mb-1 text-gray-300">จำนวนลูกเต๋าฝ่ายตั้งรับ</label>
                      <input 
                        v-model.number="newVillager.defenseDice" 
                        type="number" 
                        min="0"
                        class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                    </div>
                  </div>
                </div>

                <!-- Weapons & Perks Tab -->
                <div v-if="activeTab === 'weapons'" class="space-y-4">
                  <!-- อาวุธ -->
                  <div class="space-y-4 mb-6">
                    <h4 class="text-lg font-medium text-gray-300">อาวุธ</h4>
                    <select 
                      v-model="newVillager.weapon" 
                      class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">เลือกอาวุธ</option>
                      <option v-for="weapon in weapons" :key="weapon.value" :value="weapon.value">
                        {{ weapon.label }}
                      </option>
                    </select>
                  </div>

                  <!-- อาวุธที่ชนะทาง -->
                  <div class="space-y-4 mb-6">
                    <h4 class="text-lg font-medium text-gray-300">อาวุธที่ชนะทาง (เลือกหลาย)</h4>
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <label v-for="weapon in weapons" :key="weapon.value" class="flex items-center text-gray-300">
                        <input type="checkbox" v-model="newVillager.strongAgainst" :value="weapon.value" class="mr-2 form-checkbox h-5 w-5 text-indigo-600 bg-gray-700 border-gray-600 rounded focus:ring-indigo-500">
                        {{ weapon.label }}
                      </label>
                    </div>
                  </div>

                  <!-- อาวุธที่แพ้ทาง -->
                  <div class="space-y-4 mb-6">
                    <h4 class="text-lg font-medium text-gray-300">อาวุธที่แพ้ทาง (เลือกหลาย)</h4>
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <label v-for="weapon in weapons" :key="weapon.value" class="flex items-center text-gray-300">
                        <input type="checkbox" v-model="newVillager.weakAgainst" :value="weapon.value" class="mr-2 form-checkbox h-5 w-5 text-indigo-600 bg-gray-700 border-gray-600 rounded focus:ring-indigo-500">
                        {{ weapon.label }}
                      </label>
                    </div>
                  </div>

                  <!-- Perks -->
                  <div class="space-y-4 mb-6">
                    <h4 class="text-lg font-medium text-gray-300">Perks (เลือกได้หลายตัว)</h4>
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <label v-for="(perk, index) in availablePerks" :key="index" class="flex items-center text-gray-300">
                        <input type="checkbox" v-model="newVillager.perks" :value="perk.name" class="mr-2 form-checkbox h-5 w-5 text-indigo-600 bg-gray-700 border-gray-600 rounded focus:ring-indigo-500">
                        {{ perk.name }} - {{ perk.description }}
                      </label>
                    </div>
                  </div>
                </div>

                <!-- Upgrade Items Tab -->
                <div v-if="activeTab === 'upgrade'" class="space-y-4">
                  <h4 class="text-lg font-medium text-gray-300">ไอเท็มสำหรับอัปเกรดระดับ (เหมือนกันทุกเลเวล)</h4>
                  <div v-for="(req, index) in newVillager.upgradeRequirements" :key="index" class="grid grid-cols-12 items-center gap-3 animate-fade-in">
                    <div class="col-span-5">
                      <label class="block text-sm font-medium mb-1 text-gray-300">ชื่อไอเท็ม</label>
                      <input 
                        v-model="req.itemName" 
                        type="text" 
                        placeholder="ชื่อไอเท็ม เช่น เสื้อคลุมทหาร"
                        class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                    </div>
                    <div class="col-span-5">
                      <label class="block text-sm font-medium mb-1 text-gray-300">จำนวน</label>
                      <input 
                        v-model.number="req.quantity" 
                        type="number" 
                        min="1"
                        class="w-full px-3 py-2 bg-gray-700 rounded-lg text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                    </div>
                    <div class="col-span-2 mt-6">
                      <button 
                        v-if="newVillager.upgradeRequirements.length > 1"
                        @click="removeUpgradeReq(index)"
                        type="button"
                        class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-200"
                        aria-label="ลบไอเท็ม"
                      >
                        ลบ
                      </button>
                    </div>
                  </div>
                  <div class="flex gap-3 mt-3">
                    <button 
                      @click="addUpgradeReq"
                      type="button"
                      class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-200"
                    >
                      + เพิ่มไอเท็ม
                    </button>
                  </div>
                </div>
              </div>

              <!-- ปุ่มควบคุม -->
              <div class="flex justify-end gap-3">
                <button 
                  type="submit"
                  class="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-200"
                >
                  เพิ่มชาวบ้าน
                </button>
                <button 
                  @click="$emit('cancelModal')" 
                  type="button" 
                  class="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition duration-200"
                >
                  ยกเลิก
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch } from 'vue';
import { projectFirestore } from '@/firebase/config';

export default {
  emits: ['cancelModal', 'villagerAdded'],
  setup(props, { emit }) {
    const newVillager = ref({
      name: '',
      img: '',
      normalFace: '',
      phrases: [''],
      locations: [''],
      hireCosts: { silver: 0, gold: 0, diamond: 0 },
      stats: {
        stamina: 0,
        str: 0,
        int: 0,
        agi: 0,
        defense: 0,
        luck: 0,
      },
      level: 1,
      type: 'นักรบรับจ้าง',
      attackDice: 0,
      defenseDice: 0,
      weapon: '',
      strongAgainst: [],
      weakAgainst: [],
      perks: [],
      upgradeRequirements: [{ itemName: '', quantity: 1 }],
      createdAt: new Date()
    });
    const activeTab = ref('general');
    const tabs = ref([
      { id: 'general', name: 'ข้อมูลทั่วไป' },
      { id: 'phrases', name: 'คำพูด & สถานที่' },
      { id: 'stats', name: 'สถานะ & ลูกเต๋า' },
      { id: 'weapons', name: 'อาวุธ & Perks' },
      { id: 'upgrade', name: 'ไอเท็มอัปเกรด' },
    ]);

    // Available perks list
    const availablePerks = ref([
      { name: 'ผู้รอดชีวิต', description: 'ถ้าถูกฆ่าตายมีโอกาสกลับไปที่แคมป์ (โอกาส 30%)' },
      { name: 'บ้าคลั่ง', description: 'เมื่อมีเลือดต่ำกว่า 50 % มีโอกาเพิ่มลูกเต๋า 1 ลูก (โอกาส 20%)' },
      { name: 'นักวางกับดัก', description: 'เมื่ออยู่ฝ่ายป้องกันมีโอกาสเพิ่มลูกเต๋า 1 ลูก (โอกาส 20%)' },
      { name: 'นักบุกโจมตี', description: 'เมื่ออยู่ฝ่ายโจมตีมีโอกาสเพิ่มลูกเต๋า 1 โอกาส (โอกาส 20%)' },
      { name: 'อึดถึกทน', description: 'ลดดาเมจที่ได้รับ 20% (โอกาส 30%)' },
      { name: 'ฟื้นตัวไว', description: 'รักษาเลือด 10% max HP หลังรอบ (โอกาส 10%)' },
      { name: 'หลอกล่อ', description: 'มีโอกาสทำให้อีกฝ่ายลดลูกเต๋า 1 ลูก (โอกาส 20%)' },
      { name: 'ผู้มีบุญ', description: 'เมื่อ hp เหลือ 0 มีโอกาสที่จะไม่ตายและกลับมามีเลือด 20% (โอกาส 10%)' },
      { name: 'หัวหน้าทีม', description: 'Heal ทีม 10 % max HP หลังรอบ (โอกาส 10%)' },
      { name: 'ฆาตกรโรคจิต', description: 'มีโอกาส +2 dice ถ้าทีมเหลือน้อยกว่า 2 คน (โอกาส 20%)' },
      { name: 'นักเจรจา', description: 'เมื่อทอยลูกเต๋าแพ้มีโอกาสที่จะไม่โดนโจมตีเลย (โอกาส 10%)' },
      { name: 'ผู้ใช้อาวุธระยะไกล', description: '+10% crit ถ้าแต้มลูกเต๋านำมากกว่า 3 แต้ม (โอกาส 100%)' }
    ]);

    // Weapons list
    const weapons = ref([
      { value: 'ดาบใหญ่', label: 'ดาบใหญ่' },
      { value: 'ดาบคู่', label: 'ดาบคู่' },
      { value: 'มีดสั้น', label: 'มีดสั้น' },
      { value: 'ธนู', label: 'ธนู' },
      { value: 'เลื่อยไฟฟ้า', label: 'เลื่อยไฟฟ้า' },
      { value: 'หอก', label: 'หอก' },
      { value: 'ถุงมือ', label: 'ถุงมือ' },
      { value: 'ค้อน', label: 'ค้อน' },
      { value: 'ขวาน', label: 'ขวาน' },
      { value: 'กระบอง', label: 'กระบอง' }
    ]);

    // Phrases
    const addPhrase = () => {
      newVillager.value.phrases.push('');
    };

    const removePhrase = (index) => {
      if (newVillager.value.phrases.length > 1) {
        newVillager.value.phrases.splice(index, 1);
      }
    };

    // Locations
    const addLocation = () => {
      newVillager.value.locations.push('');
    };

    const removeLocation = (index) => {
      if (newVillager.value.locations.length > 1) {
        newVillager.value.locations.splice(index, 1);
      }
    };

    // Upgrade Requirements
    const addUpgradeReq = () => {
      newVillager.value.upgradeRequirements.push({ itemName: '', quantity: 1 });
    };

    const removeUpgradeReq = (index) => {
      if (newVillager.value.upgradeRequirements.length > 1) {
        newVillager.value.upgradeRequirements.splice(index, 1);
      }
    };

    // Presets
    const namePresets = {
      'เบญจา': {
        weapon: 'ถุงมือ',
        strongAgainst: ['หอก', 'เลื่อยไฟฟ้า', 'ธนู'],
        weakAgainst: ['ขวาน', 'ค้อน', 'ดาบใหญ่'],
        perks: ['ผู้รอดชีวิต', 'ผู้มีบุญ'],
      },
      'อารยา': {
        weapon: 'ค้อน',
        strongAgainst: ['ถุงมือ', 'หอก', 'ธนู'],
        weakAgainst: ['ดาบคู่', 'ดาบใหญ่', 'กระบอง'],
        perks: ['ผู้รอดชีวิต', 'ฟื้นตัวไว'],
      },
      'สุกิจ': {
        weapon: 'ดาบคู่',
        strongAgainst: ['ค้อน', 'ธนู', 'ถุงมือ'],
        weakAgainst: ['ดาบใหญ่', 'มีดสั้น', 'เลื่อยไฟฟ้า'],
        perks: ['ผู้รอดชีวิต', 'นักบุกโจมตี'],
      },
      'สมหมาย': {
        weapon: 'ขวาน',
        strongAgainst: ['เลื่อยไฟฟ้า', 'หอก', 'ธนู'],
        weakAgainst: ['ค้อน', 'ดาบคู่', 'ถุงมือ'],
        perks: ['ผู้รอดชีวิต', 'หัวหน้าทีม'],
      },
      'วิมลลักษณ์': {
        weapon: 'ดาบใหญ่',
        strongAgainst: ['เลื่อยไฟฟ้า', 'ขวาน', 'ค้อน'],
        weakAgainst: ['มีดสั้น', 'กระบอง', 'ดาบคู่'],
        perks: ['ผู้รอดชีวิต', 'นักเจรจา'],
      },
      'พิมพ์ชนก': {
        weapon: 'มีดสั้น',
        strongAgainst: ['ขวาน', 'ถุงมือ', 'เลื่อยไฟฟ้า'],
        weakAgainst: ['ดาบใหญ่', 'ค้อน', 'กระบอง'],
        perks: ['ผู้รอดชีวิต', 'บ้าคลั่ง'],
      },
      'ปรีชา': {
        weapon: 'กระบอง',
        strongAgainst: ['ธนู', 'มีดสั้น', 'ดาบใหญ่'],
        weakAgainst: ['ค้อน', 'ถุงมือ', 'หอก'],
        perks: ['ผู้รอดชีวิต', 'นักวางกับดัก'],
      },
      'นที': {
        weapon: 'หอก',
        strongAgainst: ['ธนู', 'ดาบใหญ่', 'ดาบคู่'],
        weakAgainst: ['กระบอง', 'เลื่อยไฟฟ้า', 'ค้อน'],
        perks: ['ผู้รอดชีวิต', 'ฆาตกรโรคจิต'],
      },
      'ธารเดช': {
        weapon: 'ธนู',
        strongAgainst: ['มีดสั้น', 'หอก', 'ถุงมือ'],
        weakAgainst: ['ค้อน', 'กระบอง', 'ขวาน'],
        perks: ['ผู้รอดชีวิต', 'อึดถึกทน', 'ผู้ใช้อาวุธระยะไกล'],
      },
      'ชาตรี': {
        weapon: 'เลื่อยไฟฟ้า',
        strongAgainst: ['ดาบคู่', 'ขวาน', 'หอก'],
        weakAgainst: ['ค้อน', 'กระบอง', 'ดาบใหญ่'],
        perks: ['ผู้รอดชีวิต', 'หลอกล่อ'],
      },
    };

    const applyNamePresetIfAny = (name) => {
      const preset = namePresets[name?.trim?.() || name];
      if (!preset) return;

      newVillager.value.weapon = preset.weapon || newVillager.value.weapon;
      newVillager.value.strongAgainst = [...(preset.strongAgainst || [])];
      newVillager.value.weakAgainst = [...(preset.weakAgainst || [])];

      const perkSet = new Set([...(newVillager.value.perks || []), ...(preset.perks || [])]);
      newVillager.value.perks = Array.from(perkSet);

      if (preset.attackDice !== undefined) {
        newVillager.value.attackDice = preset.attackDice;
      }
      if (preset.defenseDice !== undefined) {
        newVillager.value.defenseDice = preset.defenseDice;
      }

      if (preset.stats) {
        newVillager.value.stats = {
          stamina: preset.stats.stamina ?? newVillager.value.stats.stamina ?? 0,
          str: preset.stats.str ?? newVillager.value.stats.str ?? 0,
          agi: preset.stats.agi ?? newVillager.value.stats.agi ?? 0,
          int: preset.stats.int ?? newVillager.value.stats.int ?? 0,
          luck: preset.stats.luck ?? newVillager.value.stats.luck ?? 0,
          defense: preset.stats.defense ?? newVillager.value.stats.defense ?? 0,
        };
      }
    };

    watch(() => newVillager.value.name, (newName) => {
      applyNamePresetIfAny(newName);
    });

    // Handle adding new villager
    const handleAddVillager = async () => {
      try {
        const villagerName = newVillager.value.name.trim();
        if (!villagerName || /[/\.#$$$  $$]/.test(villagerName)) {
          alert('ชื่อชาวบ้านไม่ถูกต้อง ห้ามใช้ / . # $ [ ] หรือปล่อยว่าง');
          return;
        }

        // ตรวจสอบว่ามีชื่อชาวบ้านซ้ำหรือไม่
        const docRef = projectFirestore.collection('gameVillagers').doc(villagerName);
        const docSnap = await docRef.get();
        if (docSnap.exists) {
          alert('ชื่อชาวบ้านนี้มีอยู่แล้ว ไม่สามารถสร้างซ้ำได้');
          return;
        }

        // Filter valid phrases
        const filteredPhrases = newVillager.value.phrases.filter(phrase => phrase.trim());
        const filteredLocations = newVillager.value.locations.filter(location => location.trim());
        const filteredUpgradeReqs = newVillager.value.upgradeRequirements.filter(req => req.itemName.trim() && req.quantity > 0);

        const newVillagerData = {
          name: villagerName,
          img: newVillager.value.img,
          normalFace: newVillager.value.normalFace || '',
          phrases: filteredPhrases,
          locations: filteredLocations,
          hireCosts: newVillager.value.hireCosts,
          stats: {
            stamina: newVillager.value.stats.stamina,
            str: newVillager.value.stats.str,
            int: newVillager.value.stats.int,
            agi: newVillager.value.stats.agi,
            defense: newVillager.value.stats.defense,
            luck: newVillager.value.stats.luck,
          },
          level: Number(newVillager.value.level),
          type: newVillager.value.type,
          attackDice: newVillager.value.attackDice,
          defenseDice: newVillager.value.defenseDice,
          weapon: newVillager.value.weapon,
          strongAgainst: newVillager.value.strongAgainst,
          weakAgainst: newVillager.value.weakAgainst,
          perks: newVillager.value.perks,
          upgradeRequirements: filteredUpgradeReqs,
          createdAt: new Date()
        };

        // Add villager to gameVillagers collection
        await projectFirestore.collection('gameVillagers').doc(villagerName).set(newVillagerData);

        console.log('Villager added successfully:', villagerName);
        emit('villagerAdded');
        emit('cancelModal');
      } catch (error) {
        console.error('Error adding villager:', error);
        alert(`เกิดข้อผิดพลาด: ${error.message}`);
      }
    };

    return { 
      newVillager,
      activeTab,
      tabs,
      availablePerks,
      weapons,
      handleAddVillager,
      addPhrase,
      removePhrase,
      addLocation,
      removeLocation,
      addUpgradeReq,
      removeUpgradeReq
    };
  }
};
</script>

<style scoped>
.animate-fade-in {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>